/**
 * An absence's reason, said ONCE per table — client rule 2, `docs/adr/9028`, ADR 0014 as amended
 * 2026-09-30 and again 2026-10-13 (`docs/adr/9061`).
 *
 * `NumericCell` used to draw its `absent.reasonKey` as a badge beside the dash in EVERY absent
 * cell. On the exit cadence queue that is a ~280-character sentence twice per row; on exit plans
 * «Nessun valore ancora» down a whole column. The client's rule is that a table is one line per
 * row and never repeats what holds for every row.
 *
 * Inside `AbsenceNotes` (which `DataTable` puts around every table) the cell writes its absence IN
 * WORDS — «Nessuna riga», «Non confrontabile» — and nothing else visible. It registers its
 * (label, reason) pair here; the table states each DISTINCT pair once, under the table, as
 * «<label>: <reason>». There is no number and no superscript: a client read «—¹ —²» as −1 and −2
 * (ADR 9061), so the note is found by its words, not by a figure. The cell's accessible name is its
 * label, and its description (`aria-describedby`) is its note — so a screen reader still hears
 * why, in the cell, and the absence is still stated with its reason (ADR 0014), once per table
 * rather than once per cell.
 *
 * Outside `AbsenceNotes` — a hand-built table nobody has moved yet — the cell keeps the badge,
 * because a reason with nowhere else to go must not be dropped.
 */

import { createContext, useCallback, useContext, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/** One note: what the absence is called and why it holds. Deduped on the pair. */
interface AbsenceEntry {
  readonly labelKey: string;
  readonly reasonKey: string;
}

interface AbsenceNotesValue {
  /** The distinct (label, reason) pairs registered, in order of first appearance. */
  readonly entries: readonly AbsenceEntry[];
  readonly baseId: string;
  readonly register: (labelKey: string, reasonKey: string) => () => void;
}

const AbsenceNotesContext = createContext<AbsenceNotesValue | null>(null);

/** The id of a note — one per table, pointed at by every cell that carries it. */
const noteId = (baseId: string, index: number): string => `${baseId}-absence-${index}`;

/** The identity of a pair. A control character cannot occur in a catalogue key. */
const pairKey = (labelKey: string, reasonKey: string): string => `${labelKey}\u0001${reasonKey}`;

/** What an absent cell points at, when a table states the reasons. */
export interface AbsenceNoteRef {
  /** The note's element id, for `aria-describedby`. */
  readonly id: string;
}

/**
 * Registers the (`labelKey`, `reasonKey`) pair with the enclosing `AbsenceNotes` for as long as the
 * cell is mounted.
 *
 * `undefined` when there is no `AbsenceNotes` above (the caller keeps its badge); `null` when
 * there is one but this cell has no reason to state; the note otherwise.
 */
export function useAbsenceNote(labelKey: string | undefined, reasonKey: string | undefined): AbsenceNoteRef | null | undefined {
  const notes = useContext(AbsenceNotesContext);
  const register = notes?.register;
  // Layout effect: the notes are in place before the browser paints, so a table never shows a
  // frame of absences without their reasons.
  useLayoutEffect(() => {
    if (register === undefined || labelKey === undefined || reasonKey === undefined) return undefined;
    return register(labelKey, reasonKey);
  }, [register, labelKey, reasonKey]);
  if (notes === null) return undefined;
  if (labelKey === undefined || reasonKey === undefined) return null;
  const index = notes.entries.findIndex((entry) => entry.labelKey === labelKey && entry.reasonKey === reasonKey);
  // Registered in the effect, so on the very first render the pair is not in the list yet.
  if (index < 0) return null;
  return { id: noteId(notes.baseId, index) };
}

export interface AbsenceNotesProps {
  readonly children: ReactNode;
}

/**
 * Collects the absences (label and reason) of the cells inside it and states each pair once, after
 * them. Nothing is drawn when no cell is absent with a reason.
 */
export function AbsenceNotes({ children }: AbsenceNotesProps): React.JSX.Element {
  const { t } = useTranslation();
  const baseId = useId();
  const [entries, setEntries] = useState<readonly AbsenceEntry[]>([]);
  // How many mounted cells carry each pair: a note goes when its last cell does.
  const counts = useRef(new Map<string, number>());
  const register = useCallback((labelKey: string, reasonKey: string) => {
    const key = pairKey(labelKey, reasonKey);
    const same = (entry: AbsenceEntry): boolean => entry.labelKey === labelKey && entry.reasonKey === reasonKey;
    const count = counts.current.get(key) ?? 0;
    counts.current.set(key, count + 1);
    if (count === 0) setEntries((current) => (current.some(same) ? current : [...current, { labelKey, reasonKey }]));
    return () => {
      const left = (counts.current.get(key) ?? 1) - 1;
      if (left > 0) {
        counts.current.set(key, left);
        return;
      }
      counts.current.delete(key);
      setEntries((current) => current.filter((entry) => !same(entry)));
    };
  }, []);
  const value = useMemo(() => ({ entries, baseId, register }), [entries, baseId, register]);
  const titleId = `${baseId}-absence-title`;
  return (
    <AbsenceNotesContext.Provider value={value}>
      {children}
      {/* The designer's notes block (brief part 1 §1, pkgC2): a 1px ink rule above, a visible
          title in 11px 700 capitals, then one line per note at 12px with a 1.5 line height —
          the absence's own words in 600, then the sentence. The title names the list, so the
          list is still found by its name. */}
      {entries.length > 0 && (
        <div className="border-ink-primary mt-4 flex flex-col gap-1 border-t pt-3" data-absence-notes="">
          <p id={titleId} className="text-micro text-ink-primary font-bold uppercase">{t('table.absence.notes')}</p>
          <ul aria-labelledby={titleId} className="flex flex-col gap-1">
            {entries.map((entry, index) => (
              <li key={pairKey(entry.labelKey, entry.reasonKey)} id={noteId(baseId, index)} className="text-ink-primary text-[12px] leading-[1.5]">
                <span className="font-semibold">{t('table.absence.noteLabel', { label: t(entry.labelKey) })}</span>
                {' '}
                <span>{t(entry.reasonKey)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </AbsenceNotesContext.Provider>
  );
}
